import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Target, MoreVertical, Edit, BarChart3, GripVertical, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { cn, getUnitLabel } from '../../lib/utils';
import { UserAvatar } from '../UserAvatar';

const KPICard = ({
  kpi,
  isAdmin,
  isManagementMode,
  isSelected,
  onSelect,
  onClick,
  onEdit,
  onDelete,
  // Drag & drop props
  dragHandleProps,
  isDragging,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  showDragHandle = false,
}) => {
  const handleCardClick = (e) => {
    // Don't navigate if clicking on drag handle or move buttons
    if (e.target.closest('[data-drag-handle]') || e.target.closest('[data-move-buttons]')) {
      return;
    }
    if (isManagementMode) {
      onSelect(kpi.id, e);
    } else {
      onClick(kpi);
    }
  };

  const handleCheckboxChange = (checked) => {
    // Create synthetic event object for compatibility with toggleKPISelection
    onSelect(kpi.id, { stopPropagation: () => {} });
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEdit(kpi, e);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(kpi);
  };

  return (
    <Card
      className={cn(
        "card-hover cursor-pointer relative transition-all",
        isManagementMode && isSelected && "ring-2 ring-emerald-500",
        isDragging && "shadow-xl ring-2 ring-blue-500 opacity-90"
      )}
      onClick={handleCardClick}
      data-testid={`kpi-card-${kpi.id}`}
    >
      {/* Drag Handle - Desktop */}
      {showDragHandle && !isManagementMode && (
        <div 
          className="absolute top-3 left-1 z-10 hidden sm:flex"
          data-drag-handle
        >
          <button
            type="button"
            className="touch-none cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-500 transition-colors"
            {...dragHandleProps}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Move Buttons - Mobile fallback */}
      {showDragHandle && !isManagementMode && (
        <div 
          className="absolute top-3 left-1 z-10 flex sm:hidden flex-col gap-0"
          data-move-buttons
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 text-slate-400"
            onClick={(e) => { e.stopPropagation(); onMoveUp?.(kpi.id); }}
            disabled={!canMoveUp}
          >
            <ChevronUp className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 text-slate-400"
            onClick={(e) => { e.stopPropagation(); onMoveDown?.(kpi.id); }}
            disabled={!canMoveDown}
          >
            <ChevronDown className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Checkbox for management mode */}
      {isAdmin && isManagementMode && (
        <div className="absolute top-3 left-3 z-10">
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-slate-300"
            data-testid={`kpi-checkbox-${kpi.id}`}
          />
        </div>
      )}
      
      <CardContent className={cn(
        "p-4 sm:p-6", 
        isManagementMode && "pl-10 sm:pl-12",
        showDragHandle && !isManagementMode && "pl-8 sm:pl-10"
      )}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
              kpi.is_active ? 'bg-emerald-50' : 'bg-slate-100'
            )}>
              <Target className={cn('w-5 h-5', kpi.is_active ? 'text-emerald-600' : 'text-slate-400')} />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 truncate uppercase">{kpi.name}</h3>
              <p className="text-sm text-slate-500 truncate uppercase">{kpi.area}</p>
            </div>
          </div>
          
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleEditClick}>
                  <Edit className="w-4 h-4 mr-2" />Editar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDeleteClick} className="text-red-600 focus:text-red-600" data-testid={`delete-kpi-${kpi.id}`}>
                  <Trash2 className="w-4 h-4 mr-2" />Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Responsable</span>
            <div className="flex items-center gap-2 ml-2 min-w-0">
              <UserAvatar name={kpi.responsible_name} avatarUrl={kpi.responsible_avatar} size="xs" />
              <span className="text-slate-700 font-medium truncate">{kpi.responsible_name}</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Unidad</span>
            <span className="text-slate-700">{getUnitLabel(kpi.unit)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Evaluación</span>
            <Badge variant="outline" className="text-xs">
              {kpi.evaluation_type === 'percentage' ? 'Mayor mejor' : 'Menor mejor'}
            </Badge>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <Badge 
            variant={kpi.is_active ? 'default' : 'secondary'} 
            className={cn(kpi.is_active ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-50' : '')}
          >
            {kpi.is_active ? 'Activo' : 'Inactivo'}
          </Badge>
          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 hidden sm:flex">
            <BarChart3 className="w-4 h-4 mr-1" /> Ver análisis
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default KPICard;
